import { useState, useRef } from 'react';
import { PayShareType } from '../types/payShareTypes';
import { useMakeRazorpaySharePaymentMutation, useVerifyRazorpaySharePaymentMutation } from '../api/feeShareRazorpayApi';
import { showToast } from '../../../core/utils/alert';
import { getUserData } from '../../../utils/getUserData';

declare global {
    interface Window {
        Razorpay: any;
    }
}

export type PaySharePaymentPayload = {
    fee_type: string;
    center_id: number;
    session_id: number;
    course_id: number;
    amount: number;
    students: number[];
    stage_id?: number;
};

export const usePaySharePayment = (setSelectedStudentKeys: (keys: React.Key[]) => void) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const paymentIdRef = useRef<string | null>(null);
    const [makeRazorpayPayment] = useMakeRazorpaySharePaymentMutation();
    const [verifyRazorpayPayment] = useVerifyRazorpaySharePaymentMutation();

    const calculateTotalAmount = (selectedPayShares: PayShareType[]): number => {
        return selectedPayShares.reduce((total, item) => total + item.share_amount, 0);
    };

    const getPaymentPayload = (
        selectedPayShares: PayShareType[],
        feeType: string
    ): PaySharePaymentPayload | null => {
        if (selectedPayShares.length === 0) return null;

        // All selected shares should have the same session, course, and fee type
        const firstShare = selectedPayShares[0];
        const studentIds = selectedPayShares.map(share => share.id);
        const centerId = getUserData().center_id;

        const payload: PaySharePaymentPayload = {
            fee_type: feeType.toLowerCase(),
            center_id: centerId,
            session_id: Number(firstShare.session_id || 0),
            course_id: Number(firstShare.course_id || 0),
            amount: calculateTotalAmount(selectedPayShares),
            students: studentIds
        };

        // Add stage_id for course fee
        if (feeType.toLowerCase() === 'coursefee') {
            payload.stage_id = Number(firstShare.stage_id || 0);
        }

        return payload;
    };

    const initiatePayment = async (selectedPayShares: PayShareType[], feeType: string) => {
        try {
            setIsProcessing(true);
            const payload = getPaymentPayload(selectedPayShares, feeType);

            if (!payload) {
                showToast({
                    title: "Error",
                    text: "No valid payment data found",
                    icon: "error",
                });
                setIsProcessing(false);
                return;
            }

            const response = await makeRazorpayPayment(payload).unwrap();

            // console.log(response?.payment, '--inHook');

            if (response?.payment?.id) {
                paymentIdRef.current = response.payment.payment_id;
                console.log('Payment ID stored:', paymentIdRef.current);
                
                // Create Razorpay options
                const options = {
                    key: response.payment.razorpay_key,
                    amount: payload.amount,
                    currency: "INR",
                    name: "Aloha JK",
                    description: `Share Payment for ${selectedPayShares.length} student(s)`,
                    order_id: response.payment.id,
                    handler: async (razorpayResponse: any) => {
                        try {
                            await verifyPayment(razorpayResponse);
                            showToast({
                                title: "Success",
                                text: "Payment successful!",
                                icon: "success",
                            });
                        } catch (error) {
                            console.error('Payment verification failed:', error);
                            showToast({
                                title: "Error",
                                text: "Payment verification failed",
                                icon: "error",
                            });
                        }
                    },
                    // prefill: {
                    //     name: "",
                    //     email: "",
                    //     contact: ""
                    // },
                    notes: {
                        course_id: payload.course_id,
                        session_id: payload.session_id,
                        fee_type: payload.fee_type,
                        students: JSON.stringify(payload.students)
                    },
                    theme: {
                        color: "#3399cc"
                    }
                };

                // Initialize Razorpay
                const rzp = new window.Razorpay(options);
                rzp.open();
            } else {
                showToast({
                    title: "Error",
                    text: "Failed to create payment order",
                    icon: "error",
                });
            }
        } catch (error) {
            console.error('Payment initiation failed:', error);
            showToast({
                title: "Error",
                text: "Payment initiation failed",
                icon: "error",
            });
        } finally {
            setIsProcessing(false);
        }
    };
    
    // console.log(paymentIdRef.current, 'before');
    const verifyPayment = async (paymentResponse: any) => {
        try {
            if (!paymentIdRef.current) {
                throw new Error('Payment ID not found');
            }
            
            await verifyRazorpayPayment({
                paymentId: paymentIdRef.current,
                payload: {
                    razorpay_order_id: paymentResponse.razorpay_order_id,
                    razorpay_payment_id: paymentResponse.razorpay_payment_id,
                    razorpay_signature: paymentResponse.razorpay_signature
                }
            }).unwrap();
            
            paymentIdRef.current = null;
            setSelectedStudentKeys([]); 
            return true;
        } catch (error) {
            console.error('Payment verification failed:', error);
            throw error;
        }
    };

    return {
        initiatePayment,
        isProcessing,
        calculateTotalAmount
    };
}; 